import * as THREE from '@phdsoft/three';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';

/** The AO buffers run at this fraction of the composer size; the blend upsamples them bilinearly. */
export const AO_RESOLUTION_SCALE = 0.5;

/**
 * Starting GTAO parameters (model units are metres). Tuned at UAT; keep every
 * knob here rather than at the call site.
 */
export const AO_DEFAULT_PARAMETERS = {
  radius: 1.0,
  distanceExponent: 1.0,
  thickness: 1.0,
  scale: 1.0,
  samples: 12,
  distanceFallOff: 1.0,
  screenSpaceRadius: false,
};

/**
 * GTAOPass internal that exists in three 0.182's JS but is missing from (or
 * misnamed `renderPass` in) `@types/three`.
 */
interface GTAOPassInternals {
  _renderPass(
    renderer: THREE.WebGLRenderer,
    passMaterial: THREE.ShaderMaterial,
    renderTarget: THREE.WebGLRenderTarget | null,
    clearColor?: THREE.ColorRepresentation,
    clearAlpha?: number,
  ): void;
}

function scaledSize(size: number): number {
  return Math.max(1, Math.round(size * AO_RESOLUTION_SCALE));
}

/**
 * Screen-space ambient occlusion (three.js GTAO) driven by the composer's own depth.
 *
 * The stock `GTAOPass` re-renders the whole scene every frame into a private
 * normal + depth G-buffer. This pass skips that: it is put in depth-only mode
 * once (`setGBuffer` with a placeholder depth texture, so `NORMAL_VECTOR_TYPE = 0`
 * and normals are reconstructed from depth), and every frame it samples the
 * `DepthTexture` that `RenderPass` already wrote into `readBuffer`.
 *
 * The composer swaps its two buffers after every `needsSwap` pass, so `readBuffer`
 * alternates between two render targets, each with its own depth texture. The
 * depth is therefore re-bound in `render` through uniforms only — calling
 * `setGBuffer` again would rewrite `defines` and recompile the shaders.
 *
 * AO and denoise run at `AO_RESOLUTION_SCALE` of the composer size (their targets
 * use linear filtering, so the final blend upsamples). Output is
 * `GTAOPass.OUTPUT.Default`: `readBuffer` is copied to `writeBuffer` and the AO is
 * multiplied over it, so `needsSwap` stays `true`. Depth is only sampled while
 * drawing into the AO targets, never while `readBuffer` is bound, so there is no
 * framebuffer/texture feedback loop.
 *
 * If `readBuffer` has no depth texture the AO is skipped and the beauty is copied
 * through unchanged, so the next pass still finds it after the swap.
 */
export class AmbientOcclusionPass extends GTAOPass {
  /** Stand-in for the G-buffer depth; replaced by `readBuffer.depthTexture` before every draw. */
  private _placeholderDepth: THREE.DepthTexture;

  /**
   * Set per frame by Graphics (= !decision.computeAo). When true and an AO texture is cached,
   * `render` skips GTAO + denoise and only re-blends the cached `pdRenderTarget` over the beauty
   * with the current `blendIntensity`: the fade-out (camera moving, shadows intentionally stale
   * for ≤ AO_FADE_OUT_MS) and the rest of the fade-in (camera still, AO unchanged) cost one
   * full-screen copy + blend instead of the whole pass. Best effort: with nothing cached the
   * AO is computed.
   */
  public reuseAo = false;

  /** True after a full AO computation whose targets are still allocated (setSize / dispose clear it). */
  private _aoValid = false;

  public get hasCachedAo(): boolean {
    return this._aoValid;
  }

  constructor(scene: THREE.Scene, camera: THREE.Camera, width: number, height: number) {
    // GTAOPass allocates its targets from the constructor size and does not call
    // setSize itself, so the halved size must be passed here.
    super(scene, camera, scaledSize(width), scaledSize(height));

    // The super constructor already built the internal G-buffer (setGBuffer() with no
    // depth); its normalRenderTarget must stay because GTAOPass.setSize/dispose use it.
    // It is never rendered to once _renderGBuffer is false, so it never reaches the GPU.
    this._placeholderDepth = new THREE.DepthTexture(1, 1);
    this.setGBuffer(this._placeholderDepth, undefined);

    this.output = GTAOPass.OUTPUT.Default;
    this.blendIntensity = 1;
    this.updateGtaoMaterial(AO_DEFAULT_PARAMETERS);
  }

  /**
   * Sets the GTAO world radius and thickness (m) and the AO exponent `scale` for the next
   * computed AO frame; Graphics calls it per frame with ambientOcclusionWorldRadius. Uniforms
   * only, and only when a value changed: `updateGtaoMaterial` would also rewrite defines for
   * other parameters and recompile.
   * was: setWorldRadius(radius, thickness) — radius and thickness only.
   */
  public setWorldParameters(parameters: { radius: number; thickness: number; scale: number }): void {
    const uniforms = this.gtaoMaterial.uniforms;
    if (uniforms.radius.value !== parameters.radius) {
      uniforms.radius.value = parameters.radius;
    }
    if (uniforms.thickness.value !== parameters.thickness) {
      uniforms.thickness.value = parameters.thickness;
    }
    if (uniforms.scale.value !== parameters.scale) {
      uniforms.scale.value = parameters.scale;
    }
  }

  /** Receives the composer's (DPR-scaled) size and runs the AO at `AO_RESOLUTION_SCALE` of it. */
  public setSize(width: number, height: number): void {
    super.setSize(scaledSize(width), scaledSize(height));
    this._aoValid = false; // the AO targets were reallocated
  }

  public render(
    renderer: THREE.WebGLRenderer,
    writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
    deltaTime?: number,
    maskActive?: boolean,
  ): void {
    const depthTexture = readBuffer.depthTexture;

    if (!depthTexture) {
      // No depth to occlude with: pass the beauty through (needsSwap is true).
      this.copyMaterial.uniforms.tDiffuse.value = readBuffer.texture;
      this.copyMaterial.blending = THREE.NoBlending;
      (this as unknown as GTAOPassInternals)._renderPass(
        renderer, this.copyMaterial, this.renderToScreen ? null : writeBuffer,
      );
      return;
    }

    if (this.reuseAo && this._aoValid) {
      // Output stage of GTAOPass.render for OUTPUT.Default, with the cached denoised AO:
      // beauty copied to writeBuffer, then the AO multiplied over it (blendMaterial is
      // CustomBlending DstColor × Zero; intensity mixes the AO towards white).
      this.copyMaterial.uniforms.tDiffuse.value = readBuffer.texture;
      this.copyMaterial.blending = THREE.NoBlending;
      (this as unknown as GTAOPassInternals)._renderPass(
        renderer, this.copyMaterial, this.renderToScreen ? null : writeBuffer,
      );
      this.blendMaterial.uniforms.intensity.value = this.blendIntensity;
      this.blendMaterial.uniforms.tDiffuse.value = this.pdRenderTarget.texture;
      (this as unknown as GTAOPassInternals)._renderPass(
        renderer, this.blendMaterial, this.renderToScreen ? null : writeBuffer,
      );
      return;
    }

    this.gtaoMaterial.uniforms.tDepth.value = depthTexture;
    this.pdMaterial.uniforms.tDepth.value = depthTexture;
    this.depthRenderMaterial.uniforms.tDepth.value = depthTexture;

    super.render(renderer, writeBuffer, readBuffer, deltaTime, maskActive);
    this._aoValid = true;
  }

  /**
   * Compiles the full-screen AO shader programs (GTAO, Poisson denoise, copy, blend)
   * ahead of the first AO frame, so that frame does not stall on shader compilation.
   * `renderer.compile(scene)` and the outline precompile only cover scene materials.
   *
   * Uses synchronous `renderer.compile`: it issues compile + link without waiting on
   * link status (three checks that lazily on first use), so drivers with
   * KHR_parallel_shader_compile finish in the background. `compileAsync` adds only a
   * readiness poll whose `setTimeout` callback would throw, uncaught, if the pass or
   * renderer were disposed while it polls.
   *
   * A composer target is bound while compiling because the program key depends on
   * the bound target (output colour space, tone mapping) and every AO draw goes into
   * a render target. Never throws: a failure only means the first AO frame compiles.
   */
  public warmUpShaders(renderer: THREE.WebGLRenderer, renderTarget: THREE.WebGLRenderTarget): void {
    const previousTarget = renderer.getRenderTarget();
    const geometry = new THREE.PlaneGeometry(2, 2);
    const scene = new THREE.Scene();
    for (const material of [this.gtaoMaterial, this.pdMaterial, this.blendMaterial, this.copyMaterial]) {
      scene.add(new THREE.Mesh(geometry, material));
    }
    try {
      renderer.setRenderTarget(renderTarget);
      renderer.compile(scene, new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1));
    } catch (error) {
      console.warn('AmbientOcclusionPass: shader warm-up failed; the first AO frame will compile instead.', error);
    } finally {
      renderer.setRenderTarget(previousTarget);
      geometry.dispose();
    }
  }

  public dispose(): void {
    this._aoValid = false;
    super.dispose();
    // GTAOPass.dispose leaves these two materials alive.
    this.gtaoMaterial.dispose();
    this.blendMaterial.dispose();
    this._placeholderDepth.dispose();
  }
}
